/**
 * Workspace Settings Transfer
 *
 * Export and import portable workspace configuration — the "Copy Settings /
 * Paste Settings" workflow modeled on Lightroom. A workspace admin exports a
 * versioned JSON bundle from one workspace and imports it into another, choosing
 * which sections to apply (all selected by default; uncheck to omit).
 *
 * Only *portable, policy-style* configuration is included. Workspace-specific
 * record references (default workflow id, facilitator ids, etc.) are deliberately
 * excluded because they do not exist in the target workspace.
 *
 * The pure helpers in this file (build / parse / select / summarize) are
 * dependency-free so they can be unit tested without a database. The IO helpers
 * (`exportWorkspaceSettings`, `applyWorkspaceSettings`) read and write through the
 * existing admin-gated primitives so authorization is enforced server-side.
 */

import { getSupabaseClient } from './supabaseClient';
import { cycleDeleteRule, itemChangeRule, setWorkspaceSessionRules } from './workspaceSessionRules';
import {
  setWorkspaceModule,
  setWorkspaceModulePolicy,
  WORKSPACE_MODULES,
  type ModulePolicy,
} from './workspaceModulesService';
import type { BaselineTemplatePolicy } from '../types/prioritization/tenant';

// ─── Bundle format ─────────────────────────────────────────────────────────────

/** Bump when the bundle shape changes in a backwards-incompatible way. */
export const SETTINGS_BUNDLE_VERSION = 1;

export type SettingsSectionKey =
  | 'modules'
  | 'modulePolicies'
  | 'generalPreferences'
  | 'governancePolicies'
  | 'baselineTemplatePolicy';

export interface GovernancePolicies {
  sessionItemMutationPolicy?: 'lock' | 'invalidate' | 'remediate' | 'flag' | null;
  cycleDeletionPolicy?: 'block' | 'detach' | 'cascade' | null;
}

export interface WorkspaceSettingsSections {
  /** workspaces.features — module on/off toggles. */
  modules?: Record<string, boolean>;
  /** workspaces.module_policies — per-module override policy + member default. */
  modulePolicies?: Record<string, ModulePolicy>;
  /** workspaces.settings — generic preference JSON (time horizon, approvals, …). */
  generalPreferences?: Record<string, unknown>;
  /** Session-mutation + cycle-deletion governance policies. */
  governancePolicies?: GovernancePolicies;
  /** workspaces.baseline_template_policy — how Track Baselines are generated. */
  baselineTemplatePolicy?: BaselineTemplatePolicy | null;
}

export interface WorkspaceSettingsBundle {
  version: number;
  exportedAt: string;
  source: {
    workspaceId: string;
    workspaceName?: string;
    slug?: string;
  };
  sections: WorkspaceSettingsSections;
}

// ─── Section registry ──────────────────────────────────────────────────────────

export interface SettingsSectionMeta {
  key: SettingsSectionKey;
  label: string;
  description: string;
}

/** Display order is the order admins see in the import checklist. */
export const SETTINGS_SECTIONS: SettingsSectionMeta[] = [
  {
    key: 'modules',
    label: 'Platform Modules',
    description: 'Which platform modules, previews, and prioritization tools are enabled.',
  },
  {
    key: 'modulePolicies',
    label: 'Module Policies',
    description: 'Per-module override rules and the default state for members.',
  },
  {
    key: 'generalPreferences',
    label: 'General Preferences',
    description: 'Time horizon, publish approval, guest submissions, process limits, timezone.',
  },
  {
    key: 'governancePolicies',
    label: 'Governance Policies',
    description: 'Session item-mutation policy and cycle deletion policy.',
  },
  {
    key: 'baselineTemplatePolicy',
    label: 'Baseline Template Policy',
    description: 'How Track Baselines’ item scope auto-generates when a new cycle is created.',
  },
];

export function getSectionMeta(key: SettingsSectionKey): SettingsSectionMeta | undefined {
  return SETTINGS_SECTIONS.find((s) => s.key === key);
}

// ─── Pure: build ───────────────────────────────────────────────────────────────

/** Raw workspaces-row shape this module reads from / writes to. */
export interface WorkspaceSettingsRow {
  name?: string | null;
  slug?: string | null;
  features?: Record<string, boolean> | null;
  module_policies?: Record<string, ModulePolicy> | null;
  settings?: Record<string, unknown> | null;
  session_item_mutation_policy?: GovernancePolicies['sessionItemMutationPolicy'];
  cycle_deletion_policy?: GovernancePolicies['cycleDeletionPolicy'];
  baseline_template_policy?: BaselineTemplatePolicy | null;
}

/**
 * Build an export bundle from a workspaces row. A section is only included when
 * the workspace actually carries a value for it, so the importer's checklist
 * shows real, applicable choices rather than empty placeholders.
 */
export function buildSettingsBundle(
  workspaceId: string,
  row: WorkspaceSettingsRow,
  now: Date = new Date(),
): WorkspaceSettingsBundle {
  const sections: WorkspaceSettingsSections = {};

  if (row.features && Object.keys(row.features).length > 0) {
    sections.modules = { ...row.features };
  }
  if (row.module_policies && Object.keys(row.module_policies).length > 0) {
    sections.modulePolicies = { ...row.module_policies };
  }
  if (row.settings && Object.keys(row.settings).length > 0) {
    sections.generalPreferences = stripNonPortablePreferences(row.settings);
  }

  const governance: GovernancePolicies = {};
  if (row.session_item_mutation_policy != null) {
    governance.sessionItemMutationPolicy = row.session_item_mutation_policy;
  }
  if (row.cycle_deletion_policy != null) {
    governance.cycleDeletionPolicy = row.cycle_deletion_policy;
  }
  if (Object.keys(governance).length > 0) {
    sections.governancePolicies = governance;
  }

  if (row.baseline_template_policy != null) {
    sections.baselineTemplatePolicy = row.baseline_template_policy;
  }

  return {
    version: SETTINGS_BUNDLE_VERSION,
    exportedAt: now.toISOString(),
    source: {
      workspaceId,
      workspaceName: row.name ?? undefined,
      slug: row.slug ?? undefined,
    },
    sections,
  };
}

/**
 * Drop workspace-specific preference keys that should not travel between
 * workspaces (e.g. a stored demo-expiry timestamp). Everything else is portable.
 */
function stripNonPortablePreferences(settings: Record<string, unknown>): Record<string, unknown> {
  const NON_PORTABLE = new Set(['demoExpiresAt']);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(settings)) {
    if (NON_PORTABLE.has(k)) continue;
    out[k] = v;
  }
  return out;
}

// ─── Pure: parse / validate ──────────────────────────────────────────────────────

export interface ParseResult {
  bundle?: WorkspaceSettingsBundle;
  error?: string;
}

/**
 * Parse and validate raw text (typically the contents of an exported file).
 * Returns a friendly error string rather than throwing so the UI can show it.
 */
export function parseSettingsBundle(text: string): ParseResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { error: 'Not valid JSON. Make sure you pasted or selected an exported settings file.' };
  }

  if (typeof raw !== 'object' || raw === null) {
    return { error: 'Settings file is empty or malformed.' };
  }

  const obj = raw as Record<string, unknown>;

  if (typeof obj.version !== 'number') {
    return { error: 'Missing version field — this does not look like a workspace settings export.' };
  }
  if (obj.version > SETTINGS_BUNDLE_VERSION) {
    return {
      error: `This file was exported by a newer version (v${obj.version}). Update before importing.`,
    };
  }

  if (typeof obj.sections !== 'object' || obj.sections === null) {
    return { error: 'No settings sections found in this file.' };
  }

  const present = getPresentSectionKeys({ sections: obj.sections as WorkspaceSettingsSections } as WorkspaceSettingsBundle);
  if (present.length === 0) {
    return { error: 'This export contains no applicable settings.' };
  }

  const source = (typeof obj.source === 'object' && obj.source !== null ? obj.source : {}) as Record<string, unknown>;

  const bundle: WorkspaceSettingsBundle = {
    version: obj.version,
    exportedAt: typeof obj.exportedAt === 'string' ? obj.exportedAt : '',
    source: {
      workspaceId: typeof source.workspaceId === 'string' ? source.workspaceId : '',
      workspaceName: typeof source.workspaceName === 'string' ? source.workspaceName : undefined,
      slug: typeof source.slug === 'string' ? source.slug : undefined,
    },
    sections: obj.sections as WorkspaceSettingsSections,
  };

  return { bundle };
}

// ─── Pure: selection / summary ───────────────────────────────────────────────────

/** Section keys that actually carry data in the given bundle, in display order. */
export function getPresentSectionKeys(bundle: WorkspaceSettingsBundle): SettingsSectionKey[] {
  const s = bundle.sections ?? {};
  return SETTINGS_SECTIONS.map((m) => m.key).filter((key) => {
    const value = s[key];
    if (value == null) return false;
    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.keys(value as Record<string, unknown>).length > 0;
    }
    return true;
  });
}

/** A short human-readable summary of what a section will apply. */
export function summarizeSection(bundle: WorkspaceSettingsBundle, key: SettingsSectionKey): string {
  const s = bundle.sections ?? {};
  switch (key) {
    case 'modules': {
      const map = s.modules ?? {};
      const total = Object.keys(map).length;
      const on = Object.values(map).filter(Boolean).length;
      return `${total} module${total === 1 ? '' : 's'} configured · ${on} on, ${total - on} off`;
    }
    case 'modulePolicies': {
      const total = Object.keys(s.modulePolicies ?? {}).length;
      return `${total} module ${total === 1 ? 'policy' : 'policies'}`;
    }
    case 'generalPreferences': {
      const keys = Object.keys(s.generalPreferences ?? {});
      return keys.length > 0 ? keys.join(', ') : 'No preferences';
    }
    case 'governancePolicies': {
      const g = s.governancePolicies ?? {};
      const parts: string[] = [];
      if (g.sessionItemMutationPolicy != null) parts.push(`session: ${g.sessionItemMutationPolicy}`);
      if (g.cycleDeletionPolicy != null) parts.push(`cycle delete: ${g.cycleDeletionPolicy}`);
      return parts.join(' · ') || 'No policies';
    }
    case 'baselineTemplatePolicy': {
      const p = s.baselineTemplatePolicy;
      if (!p) return 'None';
      const frames = p.mode === 'custom' ? ` · ${p.customFrames?.length ?? 0} track(s)` : '';
      return `${p.mode}${frames}`;
    }
    default:
      return '';
  }
}

// ─── IO: export ──────────────────────────────────────────────────────────────────

export interface ExportResult {
  bundle?: WorkspaceSettingsBundle;
  error?: string;
}

/** Read the workspace's portable settings and build an export bundle. */
export async function exportWorkspaceSettings(workspaceId: string): Promise<ExportResult> {
  if (!workspaceId) return { error: 'No workspace selected.' };
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('workspaces')
    .select(
      'name, slug, features, module_policies, settings, session_item_mutation_policy, cycle_deletion_policy, baseline_template_policy',
    )
    .eq('id', workspaceId)
    .single();

  if (error) return { error: error.message };
  if (!data) return { error: 'Workspace not found.' };

  return { bundle: buildSettingsBundle(workspaceId, data as WorkspaceSettingsRow) };
}

// ─── IO: import / apply ──────────────────────────────────────────────────────────

export interface ApplyResult {
  applied: SettingsSectionKey[];
  errors: { section: SettingsSectionKey; message: string }[];
}

/**
 * Apply selected sections of a bundle to a target workspace.
 *
 * Module toggles and policies go through their admin-gated RPCs (one call per
 * key) — those RPCs are SECURITY DEFINER, reject non-admin callers server-side,
 * and emit a governance_events audit row on every successful change. Row-level
 * sections (general preferences / governance / baseline template) are plain
 * workspace configuration — not an authority or governance transition — so they
 * are written in a single update to the workspaces row, matching the existing
 * direct-write pattern already used for these exact columns elsewhere in the
 * app (see settings/Defaults.tsx via tenantStore.updateWorkspace). Row-level
 * writes are still gated by the workspaces RLS policy, which restricts updates
 * to workspace admins. Per-section failures are collected and returned rather
 * than aborting the whole import.
 */
export async function applyWorkspaceSettings(
  workspaceId: string,
  bundle: WorkspaceSettingsBundle,
  selectedKeys: SettingsSectionKey[],
): Promise<ApplyResult> {
  const result: ApplyResult = { applied: [], errors: [] };
  if (!workspaceId) {
    return { applied: [], errors: selectedKeys.map((section) => ({ section, message: 'No workspace selected.' })) };
  }

  const selected = new Set(selectedKeys);
  const sections = bundle.sections ?? {};

  // Modules — one RPC per known module key (ignore unknown keys defensively).
  if (selected.has('modules') && sections.modules) {
    const known = new Set(WORKSPACE_MODULES.map((m) => m.key));
    let failed = 0;
    let lastErr = '';
    for (const [key, enabled] of Object.entries(sections.modules)) {
      if (!known.has(key)) continue;
      const { error } = await setWorkspaceModule(workspaceId, key, enabled);
      if (error) {
        failed += 1;
        lastErr = error;
      }
    }
    if (failed > 0) {
      result.errors.push({ section: 'modules', message: `${failed} module(s) failed: ${lastErr}` });
    } else {
      result.applied.push('modules');
    }
  }

  // Module policies — one RPC per known module key.
  if (selected.has('modulePolicies') && sections.modulePolicies) {
    const known = new Set(WORKSPACE_MODULES.map((m) => m.key));
    let failed = 0;
    let lastErr = '';
    for (const [key, policy] of Object.entries(sections.modulePolicies)) {
      if (!known.has(key)) continue;
      const { error } = await setWorkspaceModulePolicy(workspaceId, key, policy);
      if (error) {
        failed += 1;
        lastErr = error;
      }
    }
    if (failed > 0) {
      result.errors.push({ section: 'modulePolicies', message: `${failed} policy update(s) failed: ${lastErr}` });
    } else {
      result.applied.push('modulePolicies');
    }
  }

  // Row-level sections — collected into a single workspaces update.
  const rowUpdate: Record<string, unknown> = {};
  const rowSections: SettingsSectionKey[] = [];

  if (selected.has('generalPreferences') && sections.generalPreferences) {
    rowUpdate.settings = sections.generalPreferences;
    rowSections.push('generalPreferences');
  }
  // The two session rules have one write path, which logs the change (1052).
  if (selected.has('governancePolicies') && sections.governancePolicies) {
    const g = sections.governancePolicies;
    const rules = {
      ...(g.sessionItemMutationPolicy != null && { itemChange: itemChangeRule(g.sessionItemMutationPolicy) }),
      ...(g.cycleDeletionPolicy != null && { cycleDelete: cycleDeleteRule(g.cycleDeletionPolicy) }),
    };
    if (rules.itemChange || rules.cycleDelete) {
      const saved = await setWorkspaceSessionRules(workspaceId, rules);
      if (saved.ok) result.applied.push('governancePolicies');
      else result.errors.push({ section: 'governancePolicies', message: saved.error });
    } else {
      result.applied.push('governancePolicies');
    }
  }
  if (selected.has('baselineTemplatePolicy') && sections.baselineTemplatePolicy !== undefined) {
    rowUpdate.baseline_template_policy = sections.baselineTemplatePolicy;
    rowSections.push('baselineTemplatePolicy');
  }

  if (rowSections.length > 0) {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('workspaces')
      .update({ ...rowUpdate, updated_at: new Date().toISOString() })
      .eq('id', workspaceId);
    if (error) {
      for (const section of rowSections) {
        result.errors.push({ section, message: error.message });
      }
    } else {
      result.applied.push(...rowSections);
    }
  }

  return result;
}

/** Suggested filename for a downloaded export. */
export function exportFilename(bundle: WorkspaceSettingsBundle): string {
  const base = (bundle.source.slug || bundle.source.workspaceName || 'workspace')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40) || 'workspace';
  const date = (bundle.exportedAt || new Date().toISOString()).slice(0, 10);
  return `${base}-settings-${date}.json`;
}
